/**
 * Standardized Export Utilities
 * Generic export functions for CSV, Excel, and PDF formats
 * Can be used across different modules (GL, BOM, Reports, etc.)
 */

export interface ExportOptions {
  fileName?: string;
  includeHeaders?: boolean;
  includeSummary?: boolean;
  sheetName?: string;
  title?: string;
  dateFormat?: string;
}

export interface ColumnConfig {
  key: string;
  label: string;
  format?: "text" | "currency" | "number" | "date" | "percent";
  width?: number;
}

export interface MultiSheetData<T> {
  sheetName: string;
  data: T[];
}

export interface PdfSectionData<T> {
  sectionTitle: string;
  data: T[];
}

// ==================== CSV Export ====================

/**
 * Export data to CSV format
 */
export function exportToCsv<T extends Record<string, unknown>>(
  data: T[],
  columns: ColumnConfig[],
  options: ExportOptions = {}
): void {
  const {
    fileName = `export-${Date.now()}`,
    includeHeaders = true,
  } = options;

  if (data.length === 0) {
    throw new Error("No data to export");
  }

  const csvRows: string[] = [];

  // Add headers
  if (includeHeaders) {
    const headers = columns.map((col) => escapeCSVValue(col.label));
    csvRows.push(headers.join(","));
  }

  // Add data rows
  data.forEach((row) => {
    const values = columns.map((col) => {
      const value = row[col.key];
      return escapeCSVValue(formatValue(value, col.format));
    });
    csvRows.push(values.join(","));
  });

  // Download
  const csvContent = csvRows.join("\n");
  downloadFile(csvContent, `${fileName}.csv`, "text/csv;charset=utf-8;");
}

// ==================== Excel Export ====================

/**
 * Export data to Excel format (.xlsx)
 * Requires: yarn add xlsx
 */
export async function exportToExcel<T extends Record<string, unknown>>(
  data: T[],
  columns: ColumnConfig[],
  options: ExportOptions = {}
): Promise<void> {
  const {
    fileName = `export-${Date.now()}`,
    sheetName = "Sheet1",
    includeHeaders = true,
    includeSummary = false,
    title,
  } = options;

  if (data.length === 0) {
    throw new Error("No data to export");
  }

  // Import xlsx dynamically
  const XLSX = await import("xlsx");

  // Prepare data for Excel
  const excelData: (string | number | null)[][] = [];

  // Add title if provided
  if (title) {
    excelData.push([title]);
    excelData.push([]); // Empty row
  }

  // Add headers
  if (includeHeaders) {
    excelData.push(columns.map((col) => col.label));
  }

  // Add data rows
  data.forEach((row) => {
    const values = columns.map((col) => {
      const value = row[col.key];
      // Keep numbers as numbers for Excel formatting
      if (col.format === "currency" || col.format === "number" || col.format === "percent") {
        if (typeof value === "number") return value;
        const parsed = parseFloat(String(value));
        return isNaN(parsed) ? null : parsed;
      }
      return formatValue(value, col.format);
    });
    excelData.push(values);
  });

  // Add summary row if requested
  if (includeSummary) {
    excelData.push([]); // Empty row before summary
    const summaryRow = columns.map((col, i) => {
      if (i === 0) return "TOTAL";
      if (col.format === "currency" || col.format === "number") {
        const sum = data.reduce((acc, row) => {
          const val = row[col.key];
          return acc + (typeof val === "number" ? val : parseFloat(String(val)) || 0);
        }, 0);
        return sum;
      }
      return null;
    });
    excelData.push(summaryRow);
  }

  // Create workbook and worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(excelData);

  // Set column widths
  worksheet["!cols"] = columns.map((col) => ({ wch: col.width || 15 }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // Generate and download
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
}

/**
 * Export data to Excel format with multiple sheets (.xlsx)
 * Each sheet contains data for a different group (e.g., plant)
 */
export async function exportToExcelMultiSheet<T extends Record<string, unknown>>(
  sheets: MultiSheetData<T>[],
  columns: ColumnConfig[],
  options: ExportOptions = {}
): Promise<void> {
  const {
    fileName = `export-${Date.now()}`,
    includeHeaders = true,
    includeSummary = false,
    title,
  } = options;

  if (sheets.length === 0 || sheets.every((s) => s.data.length === 0)) {
    throw new Error("No data to export");
  }

  // Import xlsx dynamically
  const XLSX = await import("xlsx");

  const workbook = XLSX.utils.book_new();

  for (const sheet of sheets) {
    if (sheet.data.length === 0) continue;

    // Prepare data for Excel
    const excelData: (string | number | null)[][] = [];

    // Add title if provided
    if (title) {
      excelData.push([`${title} - ${sheet.sheetName}`]);
      excelData.push([]); // Empty row
    }

    // Add headers
    if (includeHeaders) {
      excelData.push(columns.map((col) => col.label));
    }

    // Add data rows
    sheet.data.forEach((row) => {
      const values = columns.map((col) => {
        const value = row[col.key];
        // Keep numbers as numbers for Excel formatting
        if (col.format === "currency" || col.format === "number" || col.format === "percent") {
          if (typeof value === "number") return value;
          const parsed = parseFloat(String(value));
          return isNaN(parsed) ? null : parsed;
        }
        return formatValue(value, col.format);
      });
      excelData.push(values);
    });

    // Add summary row if requested
    if (includeSummary) {
      excelData.push([]); // Empty row before summary
      const summaryRow = columns.map((col, i) => {
        if (i === 0) return "TOTAL";
        if (col.format === "currency" || col.format === "number") {
          const sum = sheet.data.reduce((acc, row) => {
            const val = row[col.key];
            return acc + (typeof val === "number" ? val : parseFloat(String(val)) || 0);
          }, 0);
          return sum;
        }
        return null;
      });
      excelData.push(summaryRow);
    }

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(excelData);

    // Set column widths
    worksheet["!cols"] = columns.map((col) => ({ wch: col.width || 15 }));

    // Truncate sheet name to 31 chars (Excel limit)
    const safeSheetName = sheet.sheetName.slice(0, 31);
    XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName);
  }

  // Generate and download
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
}

// ==================== PDF Export ====================

/**
 * Export data to PDF format
 * Creates a printable HTML table and opens print dialog
 */
export function exportToPdf<T extends Record<string, unknown>>(
  data: T[],
  columns: ColumnConfig[],
  options: ExportOptions = {}
): void {
  const {
    fileName = `export-${Date.now()}`,
    title = "Export Report",
    includeHeaders = true,
    includeSummary = false,
  } = options;

  if (data.length === 0) {
    throw new Error("No data to export");
  }

  // Calculate totals for summary
  const totals: Record<string, number> = {};
  if (includeSummary) {
    columns.forEach((col) => {
      if (col.format === "currency" || col.format === "number") {
        totals[col.key] = data.reduce((acc, row) => {
          const val = row[col.key];
          return acc + (typeof val === "number" ? val : parseFloat(String(val)) || 0);
        }, 0);
      }
    });
  }

  // Build HTML content
  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${escapeHtml(title)}</title>
  <style>
    @media print {
      @page { margin: 0.5in; size: landscape; }
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 11px;
      line-height: 1.4;
      color: #333;
      margin: 0;
      padding: 20px;
    }
    h1 {
      font-size: 18px;
      margin: 0 0 5px 0;
      color: #1a1a1a;
    }
    .meta {
      font-size: 10px;
      color: #666;
      margin-bottom: 15px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 10px;
    }
    th {
      background-color: #f5f5f5;
      border: 1px solid #ddd;
      padding: 6px 8px;
      text-align: left;
      font-weight: 600;
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    td {
      border: 1px solid #ddd;
      padding: 5px 8px;
    }
    tr:nth-child(even) { background-color: #fafafa; }
    .number, .currency { text-align: right; font-variant-numeric: tabular-nums; }
    .summary-row { background-color: #e8e8e8 !important; font-weight: 600; }
    .footer {
      margin-top: 20px;
      font-size: 9px;
      color: #999;
      text-align: center;
    }
  </style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <div class="meta">
    Generated: ${new Date().toLocaleString()} | Records: ${data.length}
  </div>
  <table>
    ${includeHeaders ? `
    <thead>
      <tr>
        ${columns.map((col) => `<th>${escapeHtml(col.label)}</th>`).join("")}
      </tr>
    </thead>
    ` : ""}
    <tbody>
      ${data.map((row) => `
        <tr>
          ${columns.map((col) => {
            const value = row[col.key];
            const formatted = formatValue(value, col.format);
            const className = col.format === "currency" || col.format === "number" ? "number" : "";
            return `<td class="${className}">${escapeHtml(formatted)}</td>`;
          }).join("")}
        </tr>
      `).join("")}
      ${includeSummary ? `
        <tr class="summary-row">
          ${columns.map((col, i) => {
            if (i === 0) return `<td><strong>Total</strong></td>`;
            if (totals[col.key] !== undefined) {
              return `<td class="number">${formatValue(totals[col.key], col.format)}</td>`;
            }
            return `<td></td>`;
          }).join("")}
        </tr>
      ` : ""}
    </tbody>
  </table>
  <div class="footer">
    ${escapeHtml(fileName)} | IntraSync
  </div>
</body>
</html>`;

  // Open in new window for printing
  const printWindow = window.open("", "_blank");
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  } else {
    // Fallback: download as HTML
    downloadFile(html, `${fileName}.html`, "text/html");
  }
}

/**
 * Export multiple sections to PDF format with page breaks between sections
 * Creates a single printable HTML document with CSS page breaks
 */
export function exportToPdfMultiSection<T extends Record<string, unknown>>(
  sections: PdfSectionData<T>[],
  columns: ColumnConfig[],
  options: ExportOptions = {}
): void {
  const {
    fileName = `export-${Date.now()}`,
    title = "Export Report",
    includeHeaders = true,
    includeSummary = false,
  } = options;

  const nonEmptySections = sections.filter((s) => s.data.length > 0);
  if (nonEmptySections.length === 0) {
    throw new Error("No data to export");
  }

  // Build section HTML for each group
  const buildSectionHtml = (section: PdfSectionData<T>, isFirst: boolean): string => {
    // Calculate totals for this section
    const totals: Record<string, number> = {};
    if (includeSummary) {
      columns.forEach((col) => {
        if (col.format === "currency" || col.format === "number") {
          totals[col.key] = section.data.reduce((acc, row) => {
            const val = row[col.key];
            return acc + (typeof val === "number" ? val : parseFloat(String(val)) || 0);
          }, 0);
        }
      });
    }

    return `
    <div class="section${isFirst ? " first" : ""}">
      <h2>${escapeHtml(section.sectionTitle)}</h2>
      <div class="section-meta">
        Records: ${section.data.length}
      </div>
      <table>
        ${includeHeaders ? `
        <thead>
          <tr>
            ${columns.map((col) => `<th>${escapeHtml(col.label)}</th>`).join("")}
          </tr>
        </thead>
        ` : ""}
        <tbody>
          ${section.data.map((row) => `
            <tr>
              ${columns.map((col) => {
                const value = row[col.key];
                const formatted = formatValue(value, col.format);
                const className = col.format === "currency" || col.format === "number" ? "number" : "";
                return `<td class="${className}">${escapeHtml(formatted)}</td>`;
              }).join("")}
            </tr>
          `).join("")}
          ${includeSummary ? `
            <tr class="summary-row">
              ${columns.map((col, i) => {
                if (i === 0) return `<td><strong>Total</strong></td>`;
                if (totals[col.key] !== undefined) {
                  return `<td class="number">${formatValue(totals[col.key], col.format)}</td>`;
                }
                return `<td></td>`;
              }).join("")}
            </tr>
          ` : ""}
        </tbody>
      </table>
    </div>`;
  };

  // Build full HTML document
  const totalRecords = nonEmptySections.reduce((sum, s) => sum + s.data.length, 0);
  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${escapeHtml(title)}</title>
  <style>
    @media print {
      @page { margin: 0.5in; size: landscape; }
      .section { page-break-before: always; }
      .section.first { page-break-before: auto; }
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 11px;
      line-height: 1.4;
      color: #333;
      margin: 0;
      padding: 20px;
    }
    h1 {
      font-size: 18px;
      margin: 0 0 5px 0;
      color: #1a1a1a;
    }
    h2 {
      font-size: 16px;
      margin: 0 0 5px 0;
      color: #1a1a1a;
      border-bottom: 2px solid #333;
      padding-bottom: 5px;
    }
    .meta {
      font-size: 10px;
      color: #666;
      margin-bottom: 15px;
    }
    .section-meta {
      font-size: 10px;
      color: #666;
      margin-bottom: 10px;
    }
    .section {
      margin-bottom: 30px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 10px;
    }
    th {
      background-color: #f5f5f5;
      border: 1px solid #ddd;
      padding: 6px 8px;
      text-align: left;
      font-weight: 600;
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    td {
      border: 1px solid #ddd;
      padding: 5px 8px;
    }
    tr:nth-child(even) { background-color: #fafafa; }
    .number, .currency { text-align: right; font-variant-numeric: tabular-nums; }
    .summary-row { background-color: #e8e8e8 !important; font-weight: 600; }
    .footer {
      margin-top: 20px;
      font-size: 9px;
      color: #999;
      text-align: center;
    }
  </style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <div class="meta">
    Generated: ${new Date().toLocaleString()} | Total Records: ${totalRecords} | Sections: ${nonEmptySections.length}
  </div>
  ${nonEmptySections.map((section, i) => buildSectionHtml(section, i === 0)).join("")}
  <div class="footer">
    ${escapeHtml(fileName)} | IntraSync
  </div>
</body>
</html>`;

  // Open in new window for printing
  const printWindow = window.open("", "_blank");
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  } else {
    // Fallback: download as HTML
    downloadFile(html, `${fileName}.html`, "text/html");
  }
}

// ==================== Helper Functions ====================

/**
 * Format a value based on its type
 */
function formatValue(value: unknown, format?: string): string {
  if (value === null || value === undefined) {
    return "";
  }

  switch (format) {
    case "currency": {
      const numVal = typeof value === "number" ? value : parseFloat(String(value));
      if (isNaN(numVal)) return "";
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(numVal);
    }

    case "number": {
      const num = typeof value === "number" ? value : parseFloat(String(value));
      if (isNaN(num)) return "";
      return new Intl.NumberFormat("en-US").format(num);
    }

    case "percent": {
      const pct = typeof value === "number" ? value : parseFloat(String(value));
      if (isNaN(pct)) return "";
      return `${pct.toFixed(2)}%`;
    }

    case "date":
      if (value instanceof Date) {
        return value.toLocaleDateString();
      }
      if (typeof value === "string" && value.match(/^\d{4}-\d{2}-\d{2}/)) {
        return new Date(value).toLocaleDateString();
      }
      return String(value);

    default:
      return String(value);
  }
}

/**
 * Escape a value for CSV (handle commas, quotes, newlines)
 */
function escapeCSVValue(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Escape HTML special characters
 */
function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  };
  return text.replace(/[&<>"']/g, (char) => map[char]);
}

/**
 * Download a file
 */
function downloadFile(content: string, fileName: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

/**
 * Copy data to clipboard as tab-separated values
 */
export async function copyToClipboard<T extends Record<string, unknown>>(
  data: T[],
  columns: ColumnConfig[]
): Promise<void> {
  if (data.length === 0) {
    throw new Error("No data to copy");
  }

  const rows: string[] = [];

  // Headers
  rows.push(columns.map((col) => col.label).join("\t"));

  // Data
  data.forEach((row) => {
    const values = columns.map((col) => formatValue(row[col.key], col.format));
    rows.push(values.join("\t"));
  });

  const text = rows.join("\n");

  if (navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(text);
  } else {
    // Fallback for older browsers
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }
}
